import { useQuery } from "@tanstack/react-query";
import klydoApiClient from "../../api/klydo";
import collectionApiClient from "../../api/collection";
import firebaseService from "../../firebase_service/firebaseService";

const KLYDOS_REVIEW_LAST_VISIT_KEY = "reviews_klydos_last_visit";
const COLLECTIONS_REVIEW_LAST_VISIT_KEY = "reviews_collections_last_visit";

function getStorageKey(key: string) {
  const userId = firebaseService.getUserId();
  return `${key}_${userId}`;
}

function getLastVisit(key: string): number {
  const value = localStorage.getItem(getStorageKey(key));
  return value ? Number(value) : 0;
}

export function markKlydosReviewVisited() {
  localStorage.setItem(
    getStorageKey(KLYDOS_REVIEW_LAST_VISIT_KEY),
    String(Date.now()),
  );
}

export function markCollectionsReviewVisited() {
  localStorage.setItem(
    getStorageKey(COLLECTIONS_REVIEW_LAST_VISIT_KEY),
    String(Date.now()),
  );
}

function hasNewItems(
  items: { updatedAt?: Date | string }[],
  lastVisit: number,
) {
  return items.some(
    (item) => item.updatedAt && new Date(item.updatedAt).getTime() > lastVisit,
  );
}

export const useHasNewKlydosInReview = () => {
  return useQuery<boolean>({
    queryKey: ["newKlydosInReview"],
    queryFn: async () => {
      const lastVisit = getLastVisit(KLYDOS_REVIEW_LAST_VISIT_KEY);
      const items = await klydoApiClient.publicationReviewTextSearch(
        "",
        1,
        0,
        false,
        {},
        { updatedAt: -1 },
      );
      if (items.length === 0) return false;
      if (lastVisit === 0) return true;
      return hasNewItems(items, lastVisit);
    },
  });
};

export const useHasNewCollectionsInReview = () => {
  return useQuery<boolean>({
    queryKey: ["newCollectionsInReview"],
    queryFn: async () => {
      const lastVisit = getLastVisit(COLLECTIONS_REVIEW_LAST_VISIT_KEY);
      const items = await collectionApiClient.getReviewedCollections(
        "",
        0,
        1,
        false,
      );
      if (items.length === 0) return false;
      if (lastVisit === 0) return true;
      return hasNewItems(items, lastVisit);
    },
  });
};

export const useHasNewReviewItems = () => {
  const { data: hasNewKlydos } = useHasNewKlydosInReview();
  const { data: hasNewCollections } = useHasNewCollectionsInReview();
  return {
    hasNewKlydos: hasNewKlydos ?? false,
    hasNewCollections: hasNewCollections ?? false,
    hasAny: (hasNewKlydos ?? false) || (hasNewCollections ?? false),
  };
};
